import type { FC, ReactNode } from 'react'

import { cn, formatDate } from '@/lib/utils'
import { siteConfig } from '@/constants/site'

export function getInitials(name: string) {
    const cleaned = name.replace(/^(dr|prof|mr|mrs|ms)\.?\s+/i, '').trim()
    const parts = cleaned.split(/\s+/)
    const initials = (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')

    return initials.toUpperCase()
}

interface AuthorAvatarProps {
    name: string
    className?: string
}

export const AuthorAvatar: FC<AuthorAvatarProps> = ({ name, className }): ReactNode => {
    return (
        <span
            className={cn(
                'inline-grid shrink-0 place-items-center rounded-full bg-primary/10 font-heading font-semibold text-primary',
                className
            )}
            aria-hidden='true'
        >
            {getInitials(name)}
        </span>
    )
}

interface AuthorBylineProps {
    name: string | null
    role?: string | null
    date: string
    readMinutes: number
    size?: 'sm' | 'md'
    className?: string
}

export const AuthorByline: FC<AuthorBylineProps> = ({
    name,
    role,
    date,
    readMinutes,
    size = 'md',
    className
}): ReactNode => {
    const displayName = name ?? siteConfig.name

    return (
        <div className={cn('flex items-center gap-3', className)}>
            <AuthorAvatar
                name={displayName}
                className={size === 'md' ? 'size-11 text-sm' : 'size-8 text-xs'}
            />

            <div className='min-w-0 text-sm'>
                <div className='truncate font-semibold text-foreground'>
                    {displayName}
                </div>

                <div className='truncate text-muted-foreground'>
                    {role ? `${role} · ` : ''}
                    {formatDate(date)} · {readMinutes} min read
                </div>
            </div>
        </div>
    )
}
